import { useMemo } from 'react'
import { Divider, Stack } from '@mui/material'
import {
  useGetJobsLiveList,
  useGetPhasesLiveList,
} from '@/services/hooks/relay/live-lists'
import { useDocumentContext } from '@/components/image-zoom-pan/providers/DocumentProvider'
import { useProjectContext } from '@/components/project-tables/ProjectProvider'
import LineItemsTable, { LineItemsTableRow } from './LineItemsTable'
import {
  FieldItem,
  getSharedLineItemJob,
  getSharedLineItemPhase,
} from './shared'

export type PurchaseOrderDataProps = {
  vendorName: string | null
  date: string | null
  lineItems: LineItemsTableRow[]
  rowRefCallback?: (rowId: string) => (el: HTMLTableRowElement | null) => void
  highlightedRowIds?: Set<string>
  showHeaderJobNumber?: boolean
  showHeaderPhaseNumber?: boolean
}

export default function PurchaseOrderData({
  vendorName,
  date,
  lineItems,
  rowRefCallback,
  highlightedRowIds,
  showHeaderJobNumber,
  showHeaderPhaseNumber,
}: PurchaseOrderDataProps) {
  const { document } = useDocumentContext()
  const { project } = useProjectContext()

  const sharedPoJobNumber = useMemo(() => {
    if (!showHeaderJobNumber) return null
    return getSharedLineItemJob(lineItems)
  }, [lineItems, showHeaderJobNumber])
  const sharedPoPhaseNumber = useMemo(() => {
    if (!showHeaderPhaseNumber) return null
    return getSharedLineItemPhase(lineItems)
  }, [lineItems, showHeaderPhaseNumber])

  const { jobs } = useGetJobsLiveList({
    orgId: document?.owner_org_id || '',
    orgCredentialTypeId: project.org_credential_type_id || '',
    filters: sharedPoJobNumber ? { num__eq: sharedPoJobNumber } : undefined,
    enabled: showHeaderJobNumber && !!sharedPoJobNumber,
  })

  const { phases } = useGetPhasesLiveList({
    orgId: document?.owner_org_id || '',
    orgCredentialTypeId: project.org_credential_type_id || '',
    jobNum: lineItems[0]?.jobNumber || '',
    filters: sharedPoPhaseNumber ? { num__eq: sharedPoPhaseNumber } : undefined,
    enabled: showHeaderPhaseNumber && !!sharedPoPhaseNumber,
  })

  return (
    <>
      {/* Header fields */}
      <Stack
        direction="row"
        spacing={3}
        sx={{ mb: 2 }}
        useFlexGap
        flexWrap="wrap"
        alignItems="center"
      >
        <FieldItem label="Vendor" value={vendorName} />
        <FieldItem label="PO Date" value={date} />
        {showHeaderJobNumber && (
          <FieldItem
            label="Job"
            value={sharedPoJobNumber ?? null}
            tooltip={jobs[0]?.pd_display_name}
          />
        )}
        {showHeaderPhaseNumber && (
          <FieldItem
            label="Phase"
            value={sharedPoPhaseNumber ?? null}
            tooltip={phases[0]?.pd_display_name}
          />
        )}
      </Stack>

      <Divider />

      <LineItemsTable
        rows={lineItems}
        rowRefCallback={rowRefCallback}
        highlightedRowIds={highlightedRowIds}
        showJobColumn={!showHeaderJobNumber}
        showPhaseColumn={!showHeaderPhaseNumber}
      />
    </>
  )
}
